<template>
    <div>
      <!-- Company Management Dialog -->
      <v-dialog v-model="dialog" max-width="800px">
        <v-card>
          <v-card-title class="red--text d-flex justify-space-between align-center">
            <span>Manage Companies</span>
            <v-btn icon @click="closeComponent">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text>
            <v-container>
              <v-row>
                <v-col cols="12" class="d-flex justify-end">
                  <v-btn color="red darken-1" @click="addCompanyDialog = true">Add Company</v-btn>
                </v-col>
                <v-col cols="12">
                  <v-data-table
                    :headers="companyHeaders"
                    :items="companies"
                    :search="companySearch"
                    class="elevation-1"
                  >
                    <template v-slot:top>
                      <v-text-field
                        v-model="companySearch"
                        append-icon="mdi-magnify"
                        label="Search Companies"
                        single-line
                        hide-details
                      ></v-text-field>
                    </template>
                    <template v-slot:item.actions="{ item }">
                      <v-btn icon @click="openEditCompany(item)" color="blue">
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                      <v-btn icon @click="deleteCompany(item)" color="red">
                        <v-icon>mdi-delete</v-icon>
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
        </v-card>
      </v-dialog>
  
      <!-- Add Company Dialog -->
      <v-dialog v-model="addCompanyDialog" max-width="500px">
        <v-card class="rounded-card">
          <v-card-title class="red--text">Add New Company</v-card-title>
          <v-card-text>
            <v-form ref="companyForm">
              <v-text-field
                v-model="newCompanyName"
                label="Company Name"
                outlined
                dense
                rounded
                required
              ></v-text-field>
              <v-text-field
                v-model="newCompanyContactPerson"
                label="Contact Person"
                outlined
                dense
                rounded
              ></v-text-field>
              <v-text-field
                v-model="newCustomerAccount"
                label="Customer Account"
                outlined
                dense
                rounded
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="red darken-1" text @click="addCompanyDialog = false">Cancel</v-btn>
            <v-btn color="red darken-1" @click="addCompany">Add Company</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Edit Company Dialog -->
      <v-dialog v-model="editCompanyDialog" max-width="500px">
        <v-card class="rounded-card">
          <v-card-title class="red--text">Edit Company</v-card-title>
          <v-card-text>
            <v-form ref="editCompanyForm">
              <v-text-field
                v-model="editingCompany.companyName"
                label="Company Name"
                outlined
                dense
                rounded
                required
              ></v-text-field>
              <v-text-field
                v-model="editingCompany.contactPerson"
                label="Contact Person"
                outlined
                dense
                rounded
              ></v-text-field>
              <v-text-field
                v-model="editingCompany.customerAccount"
                label="Customer Account"
                outlined
                dense
                rounded
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="red darken-1" text @click="editCompanyDialog = false">Cancel</v-btn>
            <v-btn color="red darken-1" @click="updateCompany">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </template>
  
  <script>
  import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
  import { firebaseApp } from '@/firebaseConfig';
  
  export default {
    data() {
      return {
        dialog: true,
        addCompanyDialog: false,
        editCompanyDialog: false,
        companies: [],
        newCompanyName: '',
        newCompanyContactPerson: '',
        newCustomerAccount: '',
        companySearch: '',
        companyHeaders: [
          { text: 'Company Name', value: 'companyName' },
          { text: 'Contact Person', value: 'contactPerson' },
          { text: 'Customer Account', value: 'customerAccount' },
          { text: 'Actions', value: 'actions', sortable: false },
        ],
        editingCompany: {
          id: '',
          companyName: '',
          contactPerson: '',
          customerAccount: '',
        },
      };
    },
    methods: {
      closeComponent() {
        this.dialog = false;
        this.$emit('close');
      },
      async fetchCompanies() {
        const db = getFirestore(firebaseApp);
        try {
          const companiesSnapshot = await getDocs(collection(db, 'customers'));
          this.companies = companiesSnapshot.docs.map((docSnap) => ({
            id: docSnap.id,
            companyName: docSnap.data()['Company Name'],
            contactPerson: docSnap.data()['Contact Person'] || '',
            customerAccount: docSnap.data()['Customer Account'] || '',
          }));
        } catch (error) {
          console.error('Error fetching companies:', error);
        }
      },
      async addCompany() {
        if (!this.newCompanyName.trim()) {
          // Company Name is compulsory
          return;
        }
        const db = getFirestore(firebaseApp);
        try {
          await addDoc(collection(db, 'customers'), {
            'Company Name': this.newCompanyName.trim(),
            'Contact Person': this.newCompanyContactPerson.trim(),
            'Customer Account': this.newCustomerAccount.trim(),
            createdAt: new Date(),
          });
          this.addCompanyDialog = false;
          this.newCompanyName = '';
          this.newCompanyContactPerson = '';
          this.newCustomerAccount = '';
          this.fetchCompanies();
        } catch (error) {
          console.error('Error adding company:', error);
        }
      },
      openEditCompany(item) {
        this.editingCompany = { ...item };
        this.editCompanyDialog = true;
      },
      async updateCompany() {
        if (!this.editingCompany.companyName.trim()) {
          return;
        }
        const db = getFirestore(firebaseApp);
        try {
          await updateDoc(doc(db, 'customers', this.editingCompany.id), {
            'Company Name': this.editingCompany.companyName.trim(),
            'Contact Person': this.editingCompany.contactPerson.trim(),
            'Customer Account': this.editingCompany.customerAccount.trim(),
            updatedAt: new Date(),
          });
          this.editCompanyDialog = false;
          this.fetchCompanies();
        } catch (error) {
          console.error('Error updating company:', error);
        }
      },
      async deleteCompany(item) {
        const db = getFirestore(firebaseApp);
        try {
          await deleteDoc(doc(db, 'customers', item.id));
          this.fetchCompanies();
        } catch (error) {
          console.error('Error deleting company:', error);
        }
      },
    },
    mounted() {
      this.fetchCompanies();
    },
  };
  </script>
  
  <style scoped>
  .rounded-card {
    border-radius: 16px;
  }
  </style>
  